import React from "react";
import {
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";

const compactMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: Number(value) >= 1000000000 ? 2 : 1,
  }).format(Number(value) || 0);

const scoreTone = (score) => {
  if (!score) return { label: "Awaiting data", color: "#d5deed", bar: "#7f91b4" };
  if (score >= 8) return { label: "Elevated risk", color: "#ff8a72", bar: "#ff725e" };
  if (score >= 6) return { label: "Watch closely", color: "#f3c75f", bar: "#e8b83e" };
  return { label: "Healthy", color: "#5ed5a8", bar: "#3fc996" };
};

const MetricCard = ({ eyebrow, value, detail, icon, accent, action, onClick }) => (
  <Paper
    variant="outlined"
    sx={{
      minWidth: 0,
      p: { xs: 2.25, xl: 2.75 },
      borderRadius: "18px",
      borderColor: "#e4e9f2",
      boxShadow: "0 12px 30px rgba(35, 54, 91, .06)",
      position: "relative",
      overflow: "hidden",
      transition: "transform .2s ease, box-shadow .2s ease",
      "&:hover": onClick ? {
        transform: "translateY(-2px)",
        boxShadow: "0 16px 38px rgba(35, 54, 91, .1)",
      } : undefined,
    }}
  >
    <Box sx={{ position: "absolute", inset: "0 0 auto", height: 3, bgcolor: accent }} />
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: "#6c7890", fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase" }}>
          {eyebrow}
        </Typography>
        <Typography sx={{ mt: 1.15, color: "#17233f", fontSize: { xs: 29, xl: 34 }, lineHeight: 1, fontWeight: 700, letterSpacing: "-.035em" }}>
          {value}
        </Typography>
      </Box>
      <Box sx={{ width: 38, height: 38, borderRadius: "11px", display: "grid", placeItems: "center", color: accent, bgcolor: `${accent}12`, flex: "0 0 auto" }}>
        {icon}
      </Box>
    </Stack>
    <Typography sx={{ mt: 1.35, minHeight: 37, color: "#6c7890", fontSize: 13, lineHeight: 1.45 }}>
      {detail}
    </Typography>
    {action && (
      <Button
        onClick={onClick}
        endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: "16px !important" }} />}
        sx={{ mt: 1.25, p: 0, color: "#3168e0", fontSize: 12.5, fontWeight: 700, textTransform: "none" }}
      >
        {action}
      </Button>
    )}
  </Paper>
);

export default function ExecutiveOverview({
  isProspects,
  portfolioScore,
  prospectsCount,
  approvedLimit,
  existingCustomers,
  customersAtRisk,
  moneyAtRisk,
  agingUpdatedUpTo,
  onReviewRisk,
  onLoadAging,
  scoreLabel = "ArcAI portfolio score",
  intelligenceLabel,
  metricCards,
}) {
  const score = Number(portfolioScore) || 0;
  const tone = scoreTone(score);
  const atRiskShare = existingCustomers ? Math.round((customersAtRisk / existingCustomers) * 100) : 0;
  const updatedLabel = agingUpdatedUpTo
    ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(agingUpdatedUpTo))
    : "Live portfolio";

  return (
    <Box data-executive-overview component="section" aria-labelledby="executive-overview-title" sx={{ mb: 3 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "flex-end" }} spacing={1.5} sx={{ mb: 2 }}>
        <Box>
          <Typography id="executive-overview-title" sx={{ color: "#17233f", fontSize: { xs: 23, md: 27 }, fontWeight: 700, letterSpacing: "-.025em" }}>
            Portfolio command center
          </Typography>
          <Typography sx={{ mt: .4, color: "#6c7890", fontSize: 13.5 }}>
            The signals that require leadership attention, in one view.
          </Typography>
        </Box>
        <Chip
          size="small"
          icon={<InsightsRoundedIcon sx={{ fontSize: "15px !important" }} />}
          label={intelligenceLabel || `${updatedLabel} intelligence`}
          sx={{ bgcolor: "#eef4ff", color: "#2858ba", borderRadius: "8px", fontWeight: 600, "& .MuiChip-icon": { color: "#3168e0" } }}
        />
      </Stack>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: metricCards?.length > 3 ? "repeat(3, minmax(0, 1fr))" : "1.25fr repeat(3, minmax(0, 1fr))" }, gap: 2 }}>
        <Paper
          sx={{
            gridRow: { lg: metricCards?.length > 3 ? "span 2" : "auto" },
            minWidth: 0,
            minHeight: 198,
            p: { xs: 2.5, xl: 3 },
            borderRadius: "18px",
            color: "#fff",
            background: "linear-gradient(145deg, #17284f 0%, #263f77 58%, #31569c 100%)",
            boxShadow: "0 18px 42px rgba(28, 51, 98, .22)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box sx={{ position: "absolute", width: 210, height: 210, border: "1px solid rgba(255,255,255,.1)", borderRadius: "50%", right: -88, top: -78 }} />
          <Typography sx={{ color: "rgba(255,255,255,.68)", fontSize: 11, fontWeight: 700, letterSpacing: ".1em", textTransform: "uppercase" }}>
            {scoreLabel}
          </Typography>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="baseline" spacing={.75}>
              <Typography sx={{ fontSize: { xs: 48, xl: 56 }, lineHeight: 1, fontWeight: 700, letterSpacing: "-.055em" }}>
                {score.toFixed(1)}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 14 }}>/ 10</Typography>
            </Stack>
            <Chip size="small" label={tone.label} sx={{ bgcolor: "rgba(255,255,255,.1)", color: tone.color, border: "1px solid rgba(255,255,255,.14)", fontWeight: 700 }} />
          </Stack>
          <LinearProgress
            variant="determinate"
            value={Math.min(score * 10, 100)}
            sx={{ mt: 3.2, height: 7, borderRadius: 5, bgcolor: "rgba(255,255,255,.12)", "& .MuiLinearProgress-bar": { bgcolor: tone.bar, borderRadius: 5 } }}
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.1 }}>
            <Typography sx={{ color: "rgba(255,255,255,.55)", fontSize: 11.5 }}>Lower risk</Typography>
            <Button onClick={onReviewRisk} endIcon={<ArrowForwardRoundedIcon />} sx={{ p: 0, color: "#fff", fontSize: 12, fontWeight: 700, textTransform: "none", minWidth: 0 }}>
              Review drivers
            </Button>
          </Stack>
        </Paper>

        {(metricCards || [
          {
            eyebrow: isProspects ? "Pipeline" : "Risk exposure",
            value: isProspects ? prospectsCount.toLocaleString() : compactMoney(moneyAtRisk),
            detail: isProspects ? `${compactMoney(approvedLimit)} in recommended or approved capacity.` : `${customersAtRisk.toLocaleString()} counterparties require attention · ${atRiskShare}% of portfolio.`,
            accent: isProspects ? "#3168e0" : "#e25e4f",
            icon: isProspects ? <TrendingUpRoundedIcon /> : <ErrorOutlineRoundedIcon />,
            action: isProspects ? undefined : "Review risk concentration",
            onClick: onReviewRisk,
          },
          {
            eyebrow: "Counterparties",
            value: existingCustomers.toLocaleString(),
            detail: `${customersAtRisk.toLocaleString()} flagged for active monitoring across the portfolio.`,
            accent: "#5d6f98",
            icon: <BusinessRoundedIcon />,
            action: agingUpdatedUpTo ? `Data current to ${updatedLabel}` : "Update aging data",
            onClick: onLoadAging,
          },
          {
            eyebrow: "Growth pipeline",
            value: prospectsCount.toLocaleString(),
            detail: `${compactMoney(approvedLimit)} in recommended or approved prospect capacity.`,
            accent: "#35a477",
            icon: <CheckCircleRoundedIcon />,
          },
        ]).map((card) => <MetricCard key={card.eyebrow} {...card} />)}
      </Box>
    </Box>
  );
}
